// Dispatcher de pasos: slug de la URL → pantalla. Cada paso recibe el sessionId y re-lee la sesión
// del silo (fuente de verdad). Un slug desconocido cae al reanudador. Switch (no componente
// dinámico en render) para respetar react-hooks/static-components.
import { Navigate } from '@tanstack/react-router';
import { Loading } from '@/kit';
import {
    usePromotionWizardSlots,
    type PromotionWizardSlots,
} from '@/domains/retail/promotions-wizard';
import { useWizardSession } from './api/useWizard';
import { OtpStep } from './steps/OtpStep';
import { KycStep } from './steps/KycStep';
import { PhoneSelectStep } from './steps/PhoneSelectStep';
import { BuildSaleStep } from './steps/BuildSaleStep';
import { PaymentMethodStep } from './steps/PaymentMethodStep';
import { ScoreStep } from './steps/ScoreStep';
import { QuoteStep } from './steps/QuoteStep';
import { PaymentStep, type PaymentSlots } from './steps/PaymentStep';
import { DownPaymentStep } from './steps/DownPaymentStep';
import { SignStep } from './steps/SignStep';
import { ReferralsStep } from './steps/ReferralsStep';
import { DeliveryStep } from './steps/DeliveryStep';
import { CreditDeliveryStep } from './steps/CreditDeliveryStep';
import { WelcomeStep } from './steps/WelcomeStep';
import { SummaryStep, type SummarySlots } from './steps/SummaryStep';
import { AccountOpenedStep } from './steps/AccountOpenedStep';
import { hasAccountOpeningMark } from './accountOpening';
import { SaleClosedNotice } from './components/SaleClosedNotice';

/**
 * El slug 07 `payment` es compartido: contado cobra el precio full (`/payment` + `/payment/folio`)
 * y crédito cobra el ENGANCHE (`/down-payment`, folio ENG server-side). La bifurcación va ANTES de
 * montar PaymentStep porque su efecto pre-genera el folio de CONTADO — en una sesión SureKeep
 * contaminaría `session.folio` con el formato equivocado.
 */
function CobroStep({ sessionId, slots }: { sessionId: string; slots?: PaymentSlots }) {
    const { data: session } = useWizardSession(sessionId);
    if (!session) return <Loading size="lg" />;
    return session.paymentMethodKind === 'SUREKEEP' ? (
        <DownPaymentStep sessionId={sessionId} />
    ) : (
        <PaymentStep sessionId={sessionId} slots={slots} />
    );
}

/**
 * El slug 09 `delivery` también es compartido: crédito son 4 tareas (IMEI + candado MDM + app +
 * tarjeta, con gate de crédito ACTIVE) y contado son 2 (app + tarjeta).
 */
function EntregaStep({ sessionId }: { sessionId: string }) {
    const { data: session } = useWizardSession(sessionId);
    if (!session) return <Loading size="lg" />;
    return session.paymentMethodKind === 'SUREKEEP' ? (
        <CreditDeliveryStep sessionId={sessionId} />
    ) : (
        <DeliveryStep sessionId={sessionId} />
    );
}

/** El cierre de una apertura es «Cuenta abierta»; sin la marca sigue el resumen de la venta. */
function ClosingStep({ sessionId, slots }: { sessionId: string; slots?: SummarySlots }) {
    const { data: session } = useWizardSession(sessionId);
    if (!session) return <Loading size="lg" />;
    return hasAccountOpeningMark(session) ? (
        <AccountOpenedStep sessionId={sessionId} />
    ) : (
        <SummaryStep sessionId={sessionId} slots={slots} />
    );
}

export function WizardStepRouter({ sessionId, slug }: { sessionId: string; slug: string }) {
    const { data: session } = useWizardSession(sessionId);

    // Venta cancelada (o vencida, que el mapper normaliza a CANCELLED): el silo rechaza cada
    // mutación con 409 y el paso quedaba pintado sin decir por qué. `COMPLETED` no entra aquí —
    // esa terminó bien y su resumen sigue siendo legible.
    // El paso 02 resuelve su propio cierre: el rechazo de la selfie tiene pantalla.
    if (session?.state === 'CANCELLED' && slug !== 'kyc') {
        return (
            <SaleClosedNotice
                phone={session.phone}
                expired={session.expired}
                quoteDiscarded={session.quoteDiscarded}
                takenOver={session.takenOverByOtherStore}
                alreadyHasAccount={session.accountOpeningCancelReason === 'ALREADY_HAS_ACCOUNT'}
                accountOpening={hasAccountOpeningMark(session)}
            />
        );
    }

    // La apertura no vende: no monta las promociones ni pide su catálogo.
    return session && hasAccountOpeningMark(session) ? (
        <StepSwitch sessionId={sessionId} slug={slug} />
    ) : (
        <SaleStepSwitch sessionId={sessionId} slug={slug} />
    );
}

function SaleStepSwitch({ sessionId, slug }: { sessionId: string; slug: string }) {
    const promotionSlots = usePromotionWizardSlots(sessionId);
    return <StepSwitch sessionId={sessionId} slug={slug} promotionSlots={promotionSlots} />;
}

function StepSwitch({
    sessionId,
    slug,
    promotionSlots,
}: {
    sessionId: string;
    slug: string;
    promotionSlots?: PromotionWizardSlots;
}) {
    switch (slug) {
        case 'otp':
            return <OtpStep sessionId={sessionId} />;
        case 'kyc':
            return <KycStep sessionId={sessionId} />;
        case 'product':
            return <PhoneSelectStep sessionId={sessionId} slots={promotionSlots?.phoneSelect} />;
        case 'build-sale':
            return <BuildSaleStep sessionId={sessionId} slots={promotionSlots?.buildSale} />;
        case 'payment-method':
            return (
                <PaymentMethodStep sessionId={sessionId} slots={promotionSlots?.paymentMethod} />
            );
        case 'score':
            return <ScoreStep sessionId={sessionId} slots={promotionSlots?.score} />;
        case 'quote':
            return <QuoteStep sessionId={sessionId} slots={promotionSlots?.quote} />;
        case 'payment':
            return <CobroStep sessionId={sessionId} slots={promotionSlots?.payment} />;
        case 'sign':
            return <SignStep sessionId={sessionId} />;
        case 'referrals':
            return <ReferralsStep sessionId={sessionId} slots={promotionSlots?.referrals} />;
        case 'delivery':
            return <EntregaStep sessionId={sessionId} />;
        case 'welcome':
            return <WelcomeStep sessionId={sessionId} />;
        case 'summary':
            return <ClosingStep sessionId={sessionId} slots={promotionSlots?.summary} />;
        default:
            return <Navigate to="/wizard/s/$sessionId" params={{ sessionId }} replace />;
    }
}
